import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, type AdminUserRow, type UpdateUserAction } from "../api/admin";

const ROLE_COLORS: Record<string, string> = {
  ADMIN: "bg-rose-100 text-rose-800",
  MANAGER: "bg-brand-50 text-brand-700",
  VIEWER: "bg-slate-100 text-slate-700",
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: "bg-amber-100 text-amber-800",
  APPROVED: "bg-emerald-100 text-emerald-800",
  REJECTED: "bg-slate-200 text-slate-600",
};

export default function AdminUsers() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: adminApi.listUsers });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateUserAction }) =>
      adminApi.updateUser(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">Admin · Users</h2>
      {isLoading && <div className="text-slate-500">Loading…</div>}
      {data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Department</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Scope</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((u) => (
                <UserRow key={u.id} u={u} onAction={(body) => patch.mutate({ id: u.id, body })} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UserRow({
  u,
  onAction,
}: {
  u: AdminUserRow;
  onAction: (body: UpdateUserAction) => void;
}) {
  return (
    <tr>
      <td className="px-4 py-2 font-mono text-xs text-slate-700">{u.email}</td>
      <td className="px-4 py-2">{u.department ?? "—"}</td>
      <td className="px-4 py-2">
        <select
          defaultValue={u.role}
          onChange={(e) =>
            onAction({ action: "update_role", role: e.target.value as AdminUserRow["role"] })
          }
          className={`rounded px-2 py-0.5 text-xs ${ROLE_COLORS[u.role]}`}
        >
          <option>VIEWER</option>
          <option>MANAGER</option>
          <option>ADMIN</option>
        </select>
      </td>
      <td className="px-4 py-2">
        <span className={`rounded px-2 py-0.5 text-xs ${STATUS_COLORS[u.status]}`}>{u.status}</span>
      </td>
      <td className="px-4 py-2 text-xs">
        {u.role === "MANAGER" ? (
          <input
            defaultValue={u.scoped_departments.join(",")}
            placeholder="Engineering,Data"
            onBlur={(e) =>
              onAction({
                action: "update_scope",
                scoped_departments: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
              })
            }
            className="rounded border border-slate-200 px-2 py-0.5 font-mono text-xs"
          />
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-2 text-right space-x-2">
        {u.status !== "APPROVED" && (
          <button
            onClick={() => onAction({ action: "approve" })}
            className="rounded bg-emerald-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-emerald-700"
          >
            Approve
          </button>
        )}
        {u.status !== "REJECTED" && (
          <button
            onClick={() => onAction({ action: "reject" })}
            className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
          >
            Reject
          </button>
        )}
      </td>
    </tr>
  );
}
